import React from 'react';
import { Box, Typography, Chip, IconButton } from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import { FaBullseye, FaCheckCircle, FaComments } from 'react-icons/fa';
import dayjs from 'dayjs';
import { ReviewTypeBadge } from '../../../common/ReviewTypeBadge';
import {
  getReviewTypeColor,
  getReviewTypeTextColor,
  getReviewTypeText,
} from '../../../../constants/reviewTypes';
import { MEETING_REFERENCE_TYPES } from '../../../../constants/meetingReferenceTypes';
import ActionTrackingStatus from '../../../common/ActionTrackingStatus';
import { formatDateOnly, toLocalDay } from '../../../../utils/dateOnly';

/**
 * LinkedItemsSection - Displays linked items (IDP Goals, Actions, Feedback Results)
 *
 * @param {Array} linkedReferences - Array of linked reference objects
 * @param {Function} onItemClick - Callback when clicking a linked item
 * @param {Function} onDeleteClick - Callback when clicking delete (receives ref)
 * @param {boolean} canDelete - Whether delete button should be shown
 * @param {string} title - Optional custom title (default: "Linked Items")
 */
const LinkedItemsSection = ({
  linkedReferences = [],
  onItemClick,
  onDeleteClick,
  canDelete = false,
  title = 'Linked Items',
}) => {
  if (linkedReferences.length === 0) return null;

  return (
    <Box sx={{ mb: 3 }}>
      <Typography variant="subtitle2" color="text.secondary" gutterBottom>
        {title} ({linkedReferences.length})
      </Typography>
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
        {linkedReferences.map((ref) => (
          <Box
            key={ref.id}
            onClick={() => onItemClick?.(ref)}
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              p: 1.5,
              backgroundColor: '#F8FAFC',
              borderRadius: 1,
              border: '1px solid #E2E8F0',
              cursor: 'pointer',
              '&:hover': {
                backgroundColor: '#F1F5F9',
              },
            }}
          >
            {(ref.refType || ref.referenceType) === MEETING_REFERENCE_TYPES.IDP_GOAL ? (
              <FaBullseye style={{ color: ref.refEntityData?.currentStatus?.color || '#6B7280', flexShrink: 0 }} />
            ) : (ref.refType || ref.referenceType) === MEETING_REFERENCE_TYPES.IDP_ACTION ? (
              <FaCheckCircle style={{ color: ref.refEntityData?.completedAt ? '#16A34A' : '#9CA3AF', flexShrink: 0 }} />
            ) : (
              <FaComments style={{ color: '#6B7280', flexShrink: 0 }} />
            )}
            <Box sx={{ flex: 1, minWidth: 0 }}>
              {/* IDP Goal/Action Title */}
              {((ref.refType || ref.referenceType) === MEETING_REFERENCE_TYPES.IDP_GOAL || (ref.refType || ref.referenceType) === MEETING_REFERENCE_TYPES.IDP_ACTION) && (
                <Typography variant="body2" fontWeight={500} noWrap>
                  {ref.refEntityData?.title || ref.refEntityData?.name || 'Unknown'}
                  {(ref.refType || ref.referenceType) === MEETING_REFERENCE_TYPES.IDP_ACTION && ref.refEntityData?.developmentGoal?.title && (
                    <Typography component="span" variant="body2" sx={{ color: '#6B7280', fontWeight: 400 }}>
                      {' '}part of {ref.refEntityData.developmentGoal.title}
                    </Typography>
                  )}
                </Typography>
              )}
              {/* Feedback Assignment Title */}
              {(ref.refType || ref.referenceType) === MEETING_REFERENCE_TYPES.PERFORMANCE_FEEDBACK_RESULT && (
                <Typography variant="body2" fontWeight={500} noWrap>
                  {ref.refEntityData?.feedbackSession?.name ||
                   ref.refEntityData?.skillsMatrix?.name ||
                   'Feedback Assignment'}
                </Typography>
              )}
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.25, flexWrap: 'wrap' }}>
                {/* Type Label */}
                <Typography variant="caption" sx={{ color: '#6B7280' }}>
                  {(ref.refType || ref.referenceType) === MEETING_REFERENCE_TYPES.IDP_GOAL ? 'Goal' :
                   (ref.refType || ref.referenceType) === MEETING_REFERENCE_TYPES.IDP_ACTION ? 'Action' :
                   ref.refEntityData?.feedbackSession ? 'Feedback Session' : 'Individual Assignment'}
                </Typography>
                {/* Goal Status */}
                {(ref.refType || ref.referenceType) === MEETING_REFERENCE_TYPES.IDP_GOAL && ref.refEntityData?.currentStatus && (
                  <>
                    <Typography variant="caption" sx={{ color: '#9CA3AF' }}>•</Typography>
                    <Typography variant="caption" sx={{ color: '#6B7280' }}>
                      {ref.refEntityData.currentStatus.name}
                    </Typography>
                  </>
                )}
                {/* Action Status - based on tracking mode */}
                {(ref.refType || ref.referenceType) === MEETING_REFERENCE_TYPES.IDP_ACTION && (
                  <ActionTrackingStatus action={ref.refEntityData} />
                )}
                {/* Feedback Session Date */}
                {(ref.refType || ref.referenceType) === MEETING_REFERENCE_TYPES.PERFORMANCE_FEEDBACK_RESULT && ref.refEntityData?.feedbackSession && (
                  <>
                    <Typography variant="caption" sx={{ color: '#9CA3AF' }}>•</Typography>
                    <Typography variant="caption" sx={{ color: '#6B7280' }}>
                      Session of {ref.refEntityData.feedbackSession.month}/{ref.refEntityData.feedbackSession.year}
                    </Typography>
                  </>
                )}
                {/* Feedback Review Type Badge */}
                {(ref.refType || ref.referenceType) === MEETING_REFERENCE_TYPES.PERFORMANCE_FEEDBACK_RESULT &&
                 ref.refEntityData?.feedbackSession?.reviewType && (
                  <>
                    <Typography variant="caption" sx={{ color: '#9CA3AF' }}>•</Typography>
                    <ReviewTypeBadge
                      reviewType={ref.refEntityData.feedbackSession.reviewType}
                      getReviewTypeColor={getReviewTypeColor}
                      getReviewTypeTextColor={getReviewTypeTextColor}
                      getReviewTypeText={getReviewTypeText}
                      neutral
                    />
                  </>
                )}
                {/* Skills Matrix / Position for Feedback */}
                {(ref.refType || ref.referenceType) === MEETING_REFERENCE_TYPES.PERFORMANCE_FEEDBACK_RESULT &&
                 ref.refEntityData?.skillsMatrix?.position?.label && (
                  <>
                    <Typography variant="caption" sx={{ color: '#9CA3AF' }}>•</Typography>
                    <Typography variant="caption" sx={{ color: '#6B7280' }}>
                      {ref.refEntityData.skillsMatrix.position.label}
                    </Typography>
                  </>
                )}
                {ref.meetingRelationType && (
                  <>
                    <Typography variant="caption" sx={{ color: '#9CA3AF' }}>•</Typography>
                    <Chip
                      label={ref.meetingRelationType.name}
                      size="small"
                      variant="outlined"
                      sx={{
                        height: 18,
                        fontSize: '10px',
                        borderColor: ref.meetingRelationType.color || '#9CA3AF',
                        color: ref.meetingRelationType.color || '#6B7280',
                      }}
                    />
                  </>
                )}
                {ref.refEntityData?.employee?.fullName && (
                  <>
                    <Typography variant="caption" sx={{ color: '#9CA3AF' }}>•</Typography>
                    <Typography variant="caption" sx={{ color: '#6B7280' }}>
                      {ref.refEntityData.employee.fullName}
                    </Typography>
                  </>
                )}
                {/* Due Date (for IDP Goals and Actions) */}
                {((ref.refType || ref.referenceType) === MEETING_REFERENCE_TYPES.IDP_GOAL || (ref.refType || ref.referenceType) === MEETING_REFERENCE_TYPES.IDP_ACTION) &&
                 ref.refEntityData?.deadlineAt && (
                  (() => {
                    const isOverdue = toLocalDay(ref.refEntityData.deadlineAt)?.isBefore(dayjs().startOf('day')) &&
                      !ref.refEntityData.completedAt;
                    return (
                      <>
                        <Typography variant="caption" sx={{ color: '#9CA3AF' }}>•</Typography>
                        <Typography
                          variant="caption"
                          sx={{
                            color: isOverdue ? '#dc2626' : '#6B7280',
                            fontWeight: isOverdue ? 500 : 400,
                          }}
                        >
                          Due {formatDateOnly(ref.refEntityData.deadlineAt, 'DD/MM/YYYY')}
                        </Typography>
                      </>
                    );
                  })()
                )}
              </Box>
            </Box>
            {canDelete && (
              <IconButton
                size="small"
                onClick={(e) => {
                  e.stopPropagation();
                  onDeleteClick?.(ref);
                }}
              >
                <DeleteIcon fontSize="small" />
              </IconButton>
            )}
          </Box>
        ))}
      </Box>
    </Box>
  );
};

export default LinkedItemsSection;
